import {
    buildNodePackageWithKBToken,
    buildWebpackPackageWithKBToken, createSecretWithKBToken,
    deleteKBFile, deleteRawKBFile,
    fetchFileAPI,
    listFiles, listSecrets,
    uploadFileAPI
} from "../API/KB/Files";
import {getFilePathByKey, getNamespaceByKey} from "./CodeEditorUtils";
import {getMimeTypeByFilepath, parseSecrets} from "./utils";
import {executeNodejs} from "../API/CodeExecute";
import {insecureSecretWarning, isSecretComplex} from "./SecretsList";
import {encryptKBItem} from "../App/cryptoUtils";
import {deepClone} from "../App/utils";


export const closeTab = ({index, setLoadedFiles, setEditors, loadedFiles}) => {
    setLoadedFiles(prev => {
        if (prev.length === 1) {
            // If there's only one tab, just clear the content.
            return [];
        }

        let updatedFiles = [...prev];
        const wasSelected = updatedFiles[index].selected;

        updatedFiles.splice(index, 1);

        // If the removed tab was selected, select the next one or the previous one if it's the last.
        if (wasSelected) {
            const newIndex = index === updatedFiles.length ? index - 1 : index;
            updatedFiles = updatedFiles.map((f, i) => ({ ...f, selected: i === newIndex }));
        }

        return updatedFiles;
    });

    setEditors(prev => {
        const updatedEditors = { ...prev };
        delete updatedEditors[loadedFiles[index].Key];
        return updatedEditors;
    });
}

export const onFileDelete = async (filepath, {setLoading, updateFiles, setLoadedFiles, Alert}) => {
    // Prevent deletion of manifest.json in the Files folder
    if (filepath?.endsWith('manifest.json')) {
        Alert.setSystemAlert({ msg: `Cannot delete manifest.json as it is required for system functionality.`, type: 'warning', duration: 5000 });
        return;
    }
    
    setLoading(true);
    try {
        const namespace = filepath?.startsWith?.('Frontend') ? 'frontend' :
            filepath?.startsWith?.('Events') ? 'functions' : 'files';

        const deleteMethod = (namespace === 'files') ? deleteRawKBFile : deleteKBFile;
        await deleteMethod(filepath, namespace);

        const listData = await listFiles(namespace);
        
        if (namespace === 'files') {
            if (listData && listData.length > 0) {
                const filesStructure = {
                    folder: 'Files',
                    children: listData.map(file => ({
                        ...file,
                        filename: file.filename,
                        Size: file.Size,
                        Key: file.Key,
                        LastModified: file.LastModified,
                        isUserResource: true
                    }))
                };
                updateFiles(namespace, [filesStructure]);
            } else {
                // If no files left, pass empty array to remove Files folder
                updateFiles(namespace, []);
            }
        } else {
            updateFiles(namespace, listData);
            setLoadedFiles(prev => prev.filter(file => getFilePathByKey(file.Key) !== filepath));
        }

        Alert.setSystemAlert({ msg: `File "${filepath}" has been successfully deleted.`, type: 'success', duration: 3000 });

    } catch (error) {
        console.error("Error deleting file:", error);
        Alert.setSystemAlert({ msg: `Failed to delete file "${filepath}". Please try again.`, type: 'error', duration: 5000 });
    } finally {
        setLoading(false);
    }
}

export const onFileCreate = async ({ folder, children, setBlockingLoading, Alert, handleSave, isFolder, showPrompt}) => {
    const entity = isFolder ? 'Folder' : 'File';
    let newFilename = await showPrompt({
        title: `Create New ${entity}`,
        label: `${entity} Name`,
        disableMultiline: true,
        confirmText: 'Create'
    });

    if (!newFilename) return;
    if (isFolder) newFilename += '/';
    if (!children || !folder) throw new Error('Invalid data structure');

    const originalKey = children && children.find(o => o.Key).Key;
    if (!originalKey) throw new Error('No children found or first child does not have a Key');

    const folderIndex = originalKey.lastIndexOf(`/${folder}/`);
    if (folderIndex === -1) throw new Error(`Folder '${folder}' not found in the Key`);

    const basePath = originalKey.substring(0, folderIndex + folder.length + 1);
    const newFileKey = basePath + '/' + newFilename;

    // @Todo to handle exampleFunctionContent according to the parent folder
    const exampleFunctionContent = `const handler = async () => {    
    try {
        return await openkbs.googleSearch('Breaking news')
    } catch (e) {
        return e?.response?.data
    }    
}
        
module.exports = { handler }
`
    setBlockingLoading(true)
    await handleSave({
        injectedContent: (newFilename?.endsWith('.js') ? exampleFunctionContent : ''),
        newFileKey,
    })
    Alert.setSystemAlert({ msg: `File "${newFilename}" created.`, type: 'success', duration: 4000 });
    setBlockingLoading(false)
}

export const onOpenFile = async ({fileItem, reload = true, selectFileByKey, loadedFiles, setFileItemToCreate, setLoadedFiles}) => {
    const Key = fileItem.Key;

    selectFileByKey(Key)

    const found = loadedFiles.find(o => o.Key === Key)

    if (!found || reload) {

        if (!found) setLoadedFiles((prev) => [
            ...prev.map(o => ({ ...o, selected: false })),
            { selected: true, content: '', tabIsLoading: true, Key, isSaved: true }
        ]);

        const namespace = getNamespaceByKey(Key);
        const filepath = getFilePathByKey(Key)

        if (fileItem.empty && !reload) {
            setFileItemToCreate(fileItem);
            return;
        }

        fetchFileAPI({ name: filepath }, namespace)
            .then(data => {
                if (data === undefined) return;

                // Convert JSON to string so that we can edit it in the Editor
                if (typeof data?.data === 'object') {
                    data.data = JSON.stringify(data.data, null, 2)
                }

                setLoadedFiles((prev) =>
                    prev.map(o => o?.Key === Key ? { ...o, content: data.data, tabIsLoading: false } : o))

            })
            .catch(error => {
                // Create New File
                if (error?.response?.status === 404) {
                    setFileItemToCreate(fileItem);
                }
            });
    } else {
        setLoadedFiles((prev) => {
            return prev.map(f => {
                return { ...f, selected: f.Key === Key }
            });
        });
    }
}

export const createHandleSave = ({
    selectedFile,
    setDeployTooltipOpen,
    setLoadedFiles,
    updateFiles,
    setSaving,
    setDiffs,
    updateDiffsFunctions,
    fileOpenParams
}) => async ({injectedContent, newFileKey, injectedKey, withLoaders = true}) => {
    let content = selectedFile?.content || ''
    if (typeof injectedContent === 'string') {
        content = injectedContent
    }

    if (withLoaders) setSaving(true);

    const Key = newFileKey || injectedKey || selectedFile.Key

    const filepath = getFilePathByKey(Key)
    const namespace = Key.split('/')[0]

    if (namespace === 'frontend' && Key.endsWith('contentRender.js')) {
        setDeployTooltipOpen(true)
    }

    let type = getMimeTypeByFilepath(filepath);

    const fileObject = Object.assign(new Blob([content], { type }), { name: filepath });

    const res = await uploadFileAPI(fileObject, namespace, percentage => {
        if (!newFileKey) {
            setLoadedFiles((prev) => prev.map(f =>
                f.selected ? { ...f, content } : f
            ));
        }
    });

    const listData = await listFiles(namespace);
    if (newFileKey) {
        const newFileItemCreated = listData?.flatMap(item => item?.children || []).find(child => child?.Key === newFileKey);
        if (newFileItemCreated) await onOpenFile({fileItem: newFileItemCreated, ...fileOpenParams})
    }

    updateFiles(namespace, listData)
    if (withLoaders) setSaving(false);

    if (!newFileKey) {
        // Clear diffs for the saved file
        setDiffs(prev => ({ ...prev, [Key]: [] }));
        if (updateDiffsFunctions[Key]) {
            updateDiffsFunctions[Key](true);
        }
    }

    return res
}

export const createHandleKeyDown = ({selectRight, selectLeft, saveRef, deployRef, playRef}) => (event) => {
    if (event.ctrlKey && event.shiftKey && event.key === 'ArrowRight') {
        selectRight();
        event.preventDefault();
    } else if (event.ctrlKey && event.shiftKey && event.key === 'ArrowLeft') {
        selectLeft();
        event.preventDefault();
    } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        saveRef?.current?.click();
        event.preventDefault();
    } else if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'd') {
        deployRef?.current?.click();
        event.preventDefault();
    } else if ((event.ctrlKey || event.metaKey) && event.key === 'F10') {
        playRef?.current?.click();
        event.preventDefault();
    }
}

export const runCode = async ({setCodeRunLoading, selectedFile, KBData, setResponse}) => {
    try {
        setCodeRunLoading(true);
        const response = await executeNodejs(selectedFile?.content, KBData);
        setCodeRunLoading(false);

        if (Object.keys(response?.data).length === 1 && response?.data?.error) {
            setResponse(JSON.stringify(response.data));
        } else if (typeof response.data === "object") {
            setResponse(JSON.stringify(response.data));
        } else {
            setResponse(response.data);
        }
    } catch (error) {
        setCodeRunLoading(false);
        setResponse(`Error: ${error?.response?.status || error.message}`);
    }
}

export const handleDeploy = async ({selectedFile, loadedFiles, setCodeLoading, setResponse, handleSave, updateFiles}) => {
    const [namespace, KBID, folder, filename] = selectedFile?.Key.split('/')
    const moduleName = filename.split('.')[0]

    const unsavedFile = loadedFiles.find(file => !file.isSaved);

    if (unsavedFile && unsavedFile?.Key) await handleSave({})
    setCodeLoading(true);
    const build = namespace === 'frontend' ? buildWebpackPackageWithKBToken : buildNodePackageWithKBToken;
    const data = await build(namespace, moduleName);
    setCodeLoading(false);
    setResponse(data?.data)
    const listData = await listFiles(namespace);
    updateFiles(namespace, listData)
}

export const createSelectFileByKey = ({setLoadedFiles}) => (Key) => {
    setLoadedFiles(prevFiles => {
        return prevFiles.map((f) => ({ ...f, selected: Key === f.Key }));
    });
}

export const createSelectRight = ({setLoadedFiles}) => () => {
    setLoadedFiles(prevFiles => {
        const selectedIndex = prevFiles.findIndex(f => f.selected);
        const nextIndex = (selectedIndex + 1) % prevFiles.length;
        return prevFiles.map((f, i) => ({ ...f, selected: i === nextIndex }));
    });
}

export const createSelectLeft = ({setLoadedFiles}) => () => {
    setLoadedFiles(prevFiles => {
        const selectedIndex = prevFiles.findIndex(f => f.selected);
        const prevIndex = (selectedIndex - 1 + prevFiles.length) % prevFiles.length;
        return prevFiles.map((f, i) => ({ ...f, selected: i === prevIndex }));
    });
}

export const createHandleBlueprintSelect = ({
                                                handleSave, fileItemToCreate, setFileItemToCreate, fileOpenParams, selectedFile, loadedFiles, setCodeLoading,
                                                setResponse, updateFiles, kbId, showPrompt, showConfirm
                                            }) => async (code, dependenciesCode) => {
    const secrets = parseSecrets(code);
    const loadedSecrets = await listSecrets();

    for (const secret of secrets) {
        if (loadedSecrets?.data?.find?.(o => o.name === secret)) continue;

        let value;
        let confirmation = true; // Assume the user confirms by default

        do {
            value = await showPrompt({
                isSecret: true,
                title: 'Blueprint Requires Secret Values',
                label: secret,
                confirmText: 'Submit',
                cancelText: 'Skip',
                defaultValue: '',
            });

            if (value && !isSecretComplex(value)) {
                confirmation = await showConfirm({
                    title: 'Insecure Secret Warning',
                    content: insecureSecretWarning,
                });
            }

            if (confirmation) createSecretWithKBToken(secret, encryptKBItem(value, kbId));

        } while (value && !isSecretComplex(value) && !confirmation);
    }

    await handleSave({ injectedContent: code });

    if (dependenciesCode && fileItemToCreate?.Key?.endsWith('.js')) {
        const jsonFile = deepClone(fileItemToCreate).Key.replace('.js', '.json');
        await handleSave({ injectedContent: dependenciesCode, injectedKey: jsonFile });
    }

    await onOpenFile({ fileItem: fileItemToCreate, ...fileOpenParams });

    setFileItemToCreate(null);
}

export const sendVersionRestoredAlert = (Alert) =>
    Alert.setSystemAlert({ msg: `Please save your changes: You've successfully restored an older version of your function, however, these modifications are not yet saved.`, type: 'info', duration: 10000 })

